import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PxWindow } from '../components/PixelUI';
import { getWire } from '../api';
import { fmtUsd, fmtBig } from '../catalog';

const Dot = () => <span className="nv-unread">·</span>;

export const PctCell = ({ value }) => {
  if (value === null || value === undefined) return <Dot />;
  const cls = value >= 0 ? 'nv-up' : 'nv-down';
  return <span className={cls}>{value >= 0 ? '+' : ''}{value.toFixed(2)}%</span>;
};

export const TermNote = ({ children }) => (
  <p style={{ padding: '10px 18px 20px', color: 'rgba(247,240,225,.55)', fontFamily: 'VT323, monospace', fontSize: 20 }}>
    {children}
  </p>
);

const WireTable = ({ coins, onOpen }) => (
  <div style={{ overflowX: 'auto' }}>
    <table className="nv-tape-table" data-testid="wire-full-table">
      <thead>
        <tr><th>#</th><th>Coin</th><th>Price</th><th>24h</th><th>Volume 24h</th><th>Mcap</th><th></th></tr>
      </thead>
      <tbody>
        {coins.map((c, i) => (
          <tr key={c.id} className="row" onClick={() => onOpen(c.id)} data-testid={`wire-row-${c.symbol}`}>
            <td style={{ color: 'rgba(247,240,225,.45)' }}>{i + 1}</td>
            <td><span className="nv-sym">{c.symbol}</span> <span style={{ opacity: 0.6, marginLeft: 8 }}>{c.name}</span></td>
            <td>{fmtUsd(c.price) ?? <Dot />}</td>
            <td><PctCell value={c.change24h} /></td>
            <td>{fmtBig(c.volume24h) ?? <Dot />}</td>
            <td>{fmtBig(c.market_cap) ?? <Dot />}</td>
            <td style={{ color: 'var(--nv-gold)' }}>→</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const WireBody = ({ coins, onOpen }) => {
  if (coins === undefined) return <TermNote>tuning the wire…</TermNote>;
  if (coins === null) return <TermNote>the wire dropped this window — give it a minute</TermNote>;
  return <WireTable coins={coins} onOpen={onOpen} />;
};

const EthCore = ({ eth }) => {
  if (!eth) return null;
  const up = (eth.change24h ?? 0) >= 0;
  const stats = [
    ['24h', eth.change24h != null ? `${up ? '+' : ''}${eth.change24h.toFixed(2)}%` : '—', up ? 'var(--nv-green)' : '#ff6b6b'],
    ['Volume 24h', fmtBig(eth.volume24h) ?? '—', null],
    ['Market cap', fmtBig(eth.market_cap) ?? '—', null],
    ['Rank', eth.rank != null ? `#${eth.rank}` : '—', null],
  ];
  return (
    <div style={{ marginTop: 26 }}>
      <PxWindow title="ETH.CORE" tape>
        <div style={{ padding: 22 }} data-testid="eth-core-panel">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 14, marginBottom: 20 }}>
            <span className="font-pixel" style={{ fontSize: 12, color: 'var(--nv-brown)' }}>ETHEREUM</span>
            <span className="font-pixel" style={{ fontSize: 26, color: 'var(--nv-ink)' }} data-testid="eth-core-price">
              {fmtUsd(eth.price) ?? 'no signal'}
            </span>
          </div>
          <div className="nv-eth-core-grid">
            {stats.map(([k, v, tone]) => (
              <div key={k} className="nv-fleet-stat">
                <div className="k">{k}</div>
                <div className="v" style={tone ? { color: tone } : undefined}>{v}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--nv-brown)', lineHeight: 1.7, marginTop: 20 }}>
            Every NANOVRAM rental settles in ETH, so this is the only quote the desk watches.
            Dollar figures across the site are computed from this number, one-minute fresh with a
            Coinbase fallback when the ledger goes quiet.
          </p>
        </div>
      </PxWindow>
    </div>
  );
};

const Wire = () => {
  const navigate = useNavigate();
  const [coins, setCoins] = useState(undefined);

  useEffect(() => {
    getWire().then(setCoins);
  }, []);

  const eth = Array.isArray(coins) ? coins.find((c) => c.symbol === 'ETH') : null;

  return (
    <main className="nv-stars" style={{ minHeight: '80vh' }}>
      <section className="nv-section on-night" style={{ paddingTop: 40, position: 'relative', zIndex: 1 }}>
        <div className="nv-section-inner">
          <p className="nv-eyebrow">the live ETH quote, straight from the ledger · one-minute cache, Coinbase fallback</p>
          <h1 className="nv-h2" style={{ fontSize: 'clamp(26px, 5vw, 44px)' }} data-testid="wire-page-title">THE WIRE</h1>
          <PxWindow title="WIRE.ROM" tape className="nv-tape-term nv-scan">
            <p className="nv-tape-prompt">NV8&gt; mount wire.rom --eth<span className="nv-cursor" /></p>
            <WireBody coins={coins} onOpen={(id) => navigate(`/wire/${id}`)} />
            <p style={{ padding: '12px 18px', fontSize: 11, color: 'rgba(247,240,225,.45)', letterSpacing: 1 }}>
              · a dot means the feed sent no signal · open ETH to drop into its terminal
            </p>
          </PxWindow>
          <EthCore eth={eth} />
        </div>
      </section>
    </main>
  );
};

export default Wire;
